import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { useCart } from "../context/CartContext";
import { fetchProduct, money } from "../lib/api";

export default function QuickView({ slug, onClose }) {
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState({});
  const { addItem } = useCart();

  useEffect(() => {
    if (!slug) return;
    setData(null);
    setQty(1);
    setSelected({});
    fetchProduct(slug)
      .then((d) => {
        setData(d);
        const defaults = {};
        d.product.options.forEach((o) => {
          if (o.values.length) defaults[o.name] = o.values[0].label;
        });
        setSelected(defaults);
      })
      .catch(() => setData(null));
  }, [slug]);

  const product = data?.product;
  const markup = product
    ? product.options.reduce((sum, opt) => {
        const v = opt.values.find((x) => x.label === selected[opt.name]);
        return sum + (v?.markup || 0);
      }, 0)
    : 0;

  const add = () => {
    const optLabel = Object.values(selected).filter(Boolean).join(" / ") || null;
    const ecwidOptions = {};
    product.options.forEach((o) => {
      if (selected[o.name]) ecwidOptions[o.ecwid_name || o.name] = selected[o.name];
    });
    addItem({ ...product }, qty, optLabel ? { label: optLabel, markup } : null, Object.keys(ecwidOptions).length ? ecwidOptions : null);
    onClose();
  };

  return (
    <Dialog open={!!slug} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0" data-testid="quick-view-modal">
        {!product ? (
          <div className="p-10 text-center text-sm text-neutral-500">Loading...</div>
        ) : (
          <div className="grid sm:grid-cols-2">
            <div className="aspect-square bg-brand-sand">
              {product.image && <img src={product.image} alt={product.name} className="h-full w-full object-cover" />}
            </div>
            <div className="flex flex-col p-6">
              <h3 className="font-display text-xl font-black tracking-tight">{product.name}</h3>
              <p className="mt-2 text-lg font-bold" data-testid="quick-view-price">
                {money(product.price + markup)}
                {product.compare_at && (
                  <span className="ml-2 text-sm font-medium text-neutral-400 line-through">{money(product.compare_at)}</span>
                )}
              </p>
              {product.options.map((opt) => (
                <div key={opt.name} className="mt-4">
                  <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500">{opt.name}</p>
                  <div className="flex flex-wrap gap-2">
                    {opt.values.map((v) => (
                      <button
                        key={v.label}
                        onClick={() => setSelected((s) => ({ ...s, [opt.name]: v.label }))}
                        data-testid={`quick-option-${v.label}`}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                          selected[opt.name] === v.label
                            ? "border-brand-ink bg-brand-ink text-white"
                            : "border-neutral-300 hover:border-brand-ink"
                        }`}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="mt-5 flex items-center gap-3">
                <div className="flex items-center border border-neutral-300">
                  <button className="p-2 hover:bg-neutral-100" onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity">
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-8 text-center text-sm font-bold">{qty}</span>
                  <button className="p-2 hover:bg-neutral-100" onClick={() => setQty(qty + 1)} aria-label="Increase quantity">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <button
                  onClick={add}
                  disabled={!product.in_stock}
                  data-testid="quick-view-add-to-cart"
                  className="flex-1 rounded-full bg-brand-lime py-3 text-sm font-bold text-brand-ink transition-colors hover:bg-brand-limehover disabled:cursor-not-allowed disabled:bg-neutral-200"
                >
                  {product.in_stock ? "ADD TO CART" : "SOLD OUT"}
                </button>
              </div>
              <Link
                to={`/product/${product.slug}`}
                onClick={onClose}
                className="mt-4 text-center text-xs font-semibold text-neutral-500 underline-offset-4 hover:underline"
                data-testid="quick-view-full-details"
              >
                View full details
              </Link>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
